import React, { useEffect, useState } from 'react';
import { DashboardStats } from '../../types';
import { persistenceService } from '../../services/persistence';
import { AverageCard } from '..';

interface StatusPanelProps {
  stats: DashboardStats | null;
  loading: boolean;
  getCachedCryptoProgress?: () => any;
}

interface CryptoData {
  name: string;
  symbol: string;
  progress: number;
  hashrate: string;
  profitability: string;
  color: string;
  bgColor: string;
  textColor: string;
}

const StatusPanel: React.FC<StatusPanelProps> = ({ stats, loading, getCachedCryptoProgress }) => {
  const [cryptoData, setCryptoData] = useState<{
    doge: CryptoData | null;
    bitcoin: CryptoData | null;
  }>({
    doge: null,
    bitcoin: null
  });

  // Load crypto data from cache
  useEffect(() => {
    const loadCryptoData = () => {
      try {
        console.log('📦 StatusPanel: Loading crypto data from cache...');
        const data = getCachedCryptoProgress ? getCachedCryptoProgress() : persistenceService.getCryptoProgress();

        if (!data) {
          console.log('⚠️ StatusPanel: No cached crypto data available');
          return;
        }

        console.log('✅ StatusPanel: Crypto data loaded from cache:', data);

        setCryptoData({
          doge: data.doge ? {
            name: 'Dogecoin',
            symbol: 'DOGE',
            progress: data.doge.progress || 0,
            hashrate: data.doge.balance ? `${data.doge.balance.toFixed(2)} DOGE` : '---',
            profitability: `${data.doge.progress.toFixed(1)}%`,
            color: 'bg-sky-600 dark:bg-sky-500',
            bgColor: 'bg-sky-100 dark:bg-sky-500/15',
            textColor: 'text-sky-700 dark:text-sky-400'
          } : {
            name: 'Dogecoin',
            symbol: 'DOGE',
            progress: 0,
            hashrate: '---',
            profitability: '---',
            color: 'bg-sky-600 dark:bg-sky-500',
            bgColor: 'bg-sky-100 dark:bg-sky-500/15',
            textColor: 'text-sky-700 dark:text-sky-400'
          },
          bitcoin: null
        });
      } catch (error) {
        console.error('❌ StatusPanel: Failed to fetch crypto data:', error);
        // Set fallback data
        setCryptoData({
          doge: {
            name: 'Dogecoin',
            symbol: 'DOGE',
            progress: 0,
            hashrate: '---',
            profitability: '---',
            color: 'bg-sky-600 dark:bg-sky-500',
            bgColor: 'bg-sky-100 dark:bg-sky-500/15',
            textColor: 'text-sky-700 dark:text-sky-400'
          },
          bitcoin: null
        });
      }
    };

    loadCryptoData();
  }, [getCachedCryptoProgress]);

  if (loading) {
    return (
      <div className="space-y-4 short:space-y-2">
        {/* Loading Progress Bar - Only DOGE */}
        <div className="card p-5 short:p-3 animate-pulse">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gray-200 dark:bg-slate-800 rounded-full"></div>
              <div className="space-y-1">
                <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded w-20"></div>
                <div className="h-3 bg-gray-200 dark:bg-slate-800 rounded w-16"></div>
              </div>
            </div>
            <div className="h-6 bg-gray-200 dark:bg-slate-800 rounded w-16"></div>
          </div>
          <div className="h-2.5 bg-gray-200 dark:bg-slate-800 rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 short:space-y-2">
      {/* DOGE Progress Card */}
      {cryptoData.doge && (
        <div className="card p-5 short:p-3">
          <div className="flex items-center justify-between mb-4 short:mb-2">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-lg ${cryptoData.doge.bgColor} flex items-center justify-center`}>
                <span className={`text-lg font-bold ${cryptoData.doge.textColor}`}>Ð</span>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {cryptoData.doge.name}
                </h3>
                <p className="text-xs tabular-nums text-gray-500 dark:text-slate-400">
                  {cryptoData.doge.hashrate}
                </p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-xl tall:text-2xl short:text-base font-bold tabular-nums text-gray-900 dark:text-white leading-tight">
                {cryptoData.doge.profitability}
              </span>
              <p className="text-[11px] text-gray-500 dark:text-slate-400">profit</p>
            </div>
          </div>

          {/* Progress Meter */}
          <div className="relative">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-gray-500 dark:text-slate-400">Mining Progress</span>
              <span className="text-xs font-medium tabular-nums text-gray-700 dark:text-slate-300">
                {cryptoData.doge.progress}%
              </span>
            </div>
            <div className="w-full bg-sky-100 dark:bg-sky-950 rounded-full h-2.5 overflow-hidden">
              <div
                className={`h-full ${cryptoData.doge.color} rounded-full transition-all duration-700 ease-out`}
                style={{ width: `${cryptoData.doge.progress}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <AverageCard/>

    </div>
  );
};

export default StatusPanel;
